import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { deleteAlertRule, listAlertRules, updateAlertRule, type AlertRulePatch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { AlertRule } from "@/lib/types";
import Card from "@/components/Card";
import DataTable, { type Column } from "@/components/DataTable";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { tStatus, useI18n, type Key } from "@/lib/i18n";

/** Editable numeric fields, in column order; the header is a dictionary key. */
const FIELDS = [
  ["threshold", "common.threshold", "any"],
  ["hysteresis", "alerts.hysteresis", "any"],
  ["consecutive", "alerts.consecutive", "1"],
  ["cooldown_s", "alerts.cooldown", "1"],
] as const satisfies ReadonlyArray<readonly [string, Key, string]>;

type Draft = Record<(typeof FIELDS)[number][0], string>;

const draftOf = (r: AlertRule): Draft => ({
  threshold: String(r.threshold),
  hysteresis: String(r.hysteresis),
  consecutive: String(r.consecutive),
  cooldown_s: String(r.cooldown_s),
});

/**
 * Alert rules with inline editing of the anti-flap knobs. Read-only for
 * non-admins (the API rejects their writes with 403 anyway). One row edits at
 * a time; the draft lives here so the rows can be plain DataTable columns.
 */
export default function AlertRules() {
  const { t } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const qc = useQueryClient();
  const { data, error, isLoading } = useQuery({ queryKey: ["alert-rules"], queryFn: listAlertRules });
  const [edit, setEdit] = useState<{ id: number; draft: Draft } | null>(null);
  const [confirm, setConfirm] = useState<AlertRule | null>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["alert-rules"] });
    qc.invalidateQueries({ queryKey: ["alerts"] });
  };
  const save = useMutation({
    mutationFn: ({ id, body }: { id: number; body: AlertRulePatch }) => updateAlertRule(id, body),
    onSuccess: () => {
      invalidate();
      setEdit(null);
    },
  });
  const del = useMutation({
    mutationFn: (id: number) => deleteAlertRule(id),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const valid = !edit || FIELDS.every(([k]) => Number.isFinite(Number(edit.draft[k])));
  const err = (save.error ?? del.error) as Error | undefined;

  const cols: Column<AlertRule>[] = [
    {
      key: "rule", header: t("alerts.rule"), mobile: "primary",
      cell: (r) => (
        <span className={r.enabled ? "" : "opacity-60"}>
          {r.name}
          <span className="data ml-2 text-xs text-slate-500">{r.metric} {r.condition} {r.threshold}</span>
        </span>
      ),
    },
    { key: "sev", header: t("alerts.severity"), mobile: "status", cell: (r) => <StatusPill tone={r.severity}>{tStatus(t, r.severity)}</StatusPill> },
    ...FIELDS.map(([key, label, step]): Column<AlertRule> => ({
      key, header: t(label), mono: true, className: "text-right",
      cell: (r) =>
        edit?.id === r.id ? (
          <Input
            type="number"
            step={step}
            value={edit.draft[key]}
            onChange={(e) => setEdit({ id: r.id, draft: { ...edit.draft, [key]: e.target.value } })}
            className="h-7 w-24 text-right text-xs"
          />
        ) : (
          r[key]
        ),
    })),
    {
      key: "enabled", header: t("common.enabled"), className: "text-xs",
      cell: (r) =>
        admin ? (
          <label className="flex items-center gap-2 text-slate-400">
            <input
              type="checkbox"
              checked={r.enabled}
              disabled={save.isPending}
              onChange={(e) => save.mutate({ id: r.id, body: { enabled: e.target.checked } })}
            />
            {t("common.enabled")}
          </label>
        ) : r.enabled ? t("common.enabled") : <span className="text-slate-500">{t("common.disabled")}</span>,
    },
  ];
  if (admin)
    cols.push({
      key: "actions", header: "", mobile: "meta", className: "text-right",
      cell: (r) =>
        edit?.id === r.id ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              disabled={save.isPending || !valid}
              onClick={() =>
                save.mutate({ id: r.id, body: Object.fromEntries(FIELDS.map(([k]) => [k, Number(edit.draft[k])])) as AlertRulePatch })
              }
            >
              {t(save.isPending ? "common.saving" : "common.save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>{t("common.cancel")}</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="ghost" onClick={() => setEdit({ id: r.id, draft: draftOf(r) })}>{t("common.edit")}</Button>
            <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => setConfirm(r)}>{t("common.delete")}</Button>
          </>
        ),
    });

  return (
    <Card title={t("alerts.rules")}>
      {isLoading && <p className="text-slate-400">{t("common.loading")}</p>}
      {error && <p className="text-signal-crit">{(error as Error).message}</p>}
      {data && <DataTable rows={data.rules} columns={cols} rowKey={(r) => r.id} empty={t("alerts.noRules")} />}
      {err && <p className="mt-2 text-xs text-signal-crit">{err.message}</p>}
      <p className="mt-3 text-xs text-slate-500">{t("alerts.rulesHelp")}</p>
      {confirm && (
        <Dialog open onOpenChange={(o) => !o && setConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("alerts.deleteRuleTitle", { name: confirm.name })}</DialogTitle>
            </DialogHeader>
            <p className="text-slate-400">
              {t("alerts.deleteRuleBefore")} <code className="data text-slate-200">{confirm.metric}</code>{" "}
              {t("alerts.deleteRuleAfter")}
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirm(null)}>{t("common.cancel")}</Button>
              <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate(confirm.id)}>
                {t(del.isPending ? "alerts.deleting" : "common.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
